import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
} from "react";
import { hrLogin, hrLogout, hrAuthStatus } from "../api/hrUserApi";
import { notifications } from "@mantine/notifications";
import { getDepartmentSelectData } from "../api/departmentApi";
import { getRoleSelectData } from "../api/roleApi";
import { getSkillSelectData } from "../api/skillApi";
import { getContractSelectData } from "../api/contractApi";
import { getTerminalSelectData } from "../api/terminalApi";

const AuthContext = createContext<{
  user: any;
  company: any;
  isAuthenticated: any;
  employmentTypes: any;
  departmentSelectData: any;
  roleSelectData: any;
  skillSelectData: any;
  contractSelectData: any;
  terminalSelectData: any;
  login: (phone: string, password: string, companyCode: string) => void;
  logout: () => void;
  setCompany: React.Dispatch<React.SetStateAction<any>>;
  refetchDepartmentSelectData: () => void;
  refetchRoleSelectData: () => void;
  refetchSkillSelectData: () => void;
  refetchContractSelectData: () => void;
  refetchTerminalSelectData: () => void;
}>(null!);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [token, setToken] = useState<string>("");
  const hasFetched = useRef(false); // İlk isteği kontrol etmek için

  //select data
  const [employmentTypes, setEmploymentTypes] = useState();
  const [departmentSelectData, setDepartmentSelectData] = useState();
  const [roleSelectData, setRoleSelectData] = useState();
  const [skillSelectData, setSkillSelectData] = useState();
  const [contractSelectData, setContractSelectData] = useState();
  const [terminalSelectData, setTerminalSelectData] = useState();

  const fetchTerminalSelectData = async () => {
    try {
      const { data: response }: any = await getTerminalSelectData();
      setTerminalSelectData(response?.data);
    } catch {}
  };

  const fetchDepartmentSelectData = async () => {
    try {
      const { data: response }: any = await getDepartmentSelectData();
      setDepartmentSelectData(response?.data);
    } catch {}
  };

  const fetchRoleSelectData = async () => {
    try {
      const { data: response }: any = await getRoleSelectData();
      setRoleSelectData(response?.data);
    } catch {}
  };

  const fetchSkillSelectData = async () => {
    try {
      const { data: response }: any = await getSkillSelectData();
      setSkillSelectData(response?.data);
    } catch {}
  };

  const fetchContractSelectdata = async () => {
    try {
      const { data: response }: any = await getContractSelectData();
      setContractSelectData(response?.data);
    } catch {}
  };

  const authStatus = async (token: string) => {
    const res: any = await hrAuthStatus(token);
    if (res?.data?.loggedIn) {
      setToken(res?.data?.token);
      localStorage.setItem("token", res?.data?.token);
      setIsAuthenticated(true);
      setCompany(res?.data?.company);
      setEmploymentTypes(res?.data?.company?.employmentTypes);
      setUser(res?.data?.user);
      fetchDepartmentSelectData();
      fetchRoleSelectData();
      fetchSkillSelectData();
      fetchContractSelectdata();
      fetchTerminalSelectData();
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setCompany(null);
      setToken("");
      localStorage.removeItem("token");
    }
    return res;
  };

  const login = async (
    phone: string,
    password: string,
    companyCode: string
  ) => {
    const res: any = await hrLogin(phone, password, companyCode);
    if (res?.data?.success && res?.data?.token) {
      setUser(res?.data?.user);
      setCompany(res?.data?.company);
      setToken(res?.data?.token);
      localStorage.setItem("token", res?.data?.token);
      setIsAuthenticated(true);
      fetchDepartmentSelectData();
      fetchRoleSelectData();
      fetchSkillSelectData();
      fetchContractSelectdata();
      fetchTerminalSelectData();
      notifications.show({
        title: `Giriş Başarılı ${res?.data?.user.name} !`,
        message: `Hoş Geldiniz.`,
        autoClose: 4000,
        withBorder: true,
        color: "green",
      });
    } else {
      notifications.show({
        title: "Hata!",
        message: `${res.response.data.message}`,
        autoClose: 4000,
        withBorder: true,
        color: "red",
      });
    }
  };

  const logout = async () => {
    const res: any = await hrLogout(token);
    if (res.data.loggedout) {
      notifications.show({
        title: "Çıkış Başarılı " + user?.name + " !",
        message: `İyi Günler Dileriz.`,
        autoClose: 4000,
        withBorder: true,
        color: "green",
      });
      setUser(null);
      setCompany(null);
      setToken("");
      setIsAuthenticated(false);
      localStorage.removeItem("token");
    }
  };

  useEffect(() => {
    if (hasFetched.current) return; // İkinci çalışmayı engelle
    hasFetched.current = true;
    const storedToken = localStorage.getItem("token");

    if (storedToken) {
      authStatus(storedToken);
    } else {
      setIsAuthenticated(false);
    }
  }, []);

  useEffect(() => {
    if (company) {
      localStorage.setItem("company", JSON.stringify(company));
    } else {
      localStorage.removeItem("company");
    }
  }, [company]);

  return (
    <AuthContext.Provider
      value={{
        user,
        company,
        employmentTypes,
        isAuthenticated,
        departmentSelectData,
        roleSelectData,
        skillSelectData,
        contractSelectData,
        terminalSelectData,
        login,
        logout,
        setCompany,
        refetchDepartmentSelectData: fetchDepartmentSelectData,
        refetchRoleSelectData: fetchRoleSelectData,
        refetchSkillSelectData: fetchSkillSelectData,
        refetchContractSelectData: fetchContractSelectdata,
        refetchTerminalSelectData: fetchTerminalSelectData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
